import React, { useState } from "react";
import { DollarSign, Plus, ArrowUpRight, ArrowDownRight, Building2, Receipt, PieChart } from "lucide-react";
import { useApp } from "../../app/context/AppContext";
import { DataTable } from "../../components/common/DataTable";
import { Badge } from "../../components/common/Badge";
import { formatCurrency, formatDate } from "../../utils/formatters";
import { PaymentModal, ExpenseModal } from "./PaymentModal";

export function FinanceModule() {
  const { payments, expenses, metrics } = useApp();
  const [activeTab, setActiveTab] = useState("pl"); // 'pl' | 'payments' | 'expenses'
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isExpenseOpen, setIsExpenseOpen] = useState(false);

  const paymentColumns = [
    {
      header: "Payment Ref",
      accessorKey: "id",
      render: (val, row) => (
        <div>
          <span className="font-bold font-mono text-slate-900 dark:text-white">{val}</span>
          <p className="text-[10px] text-slate-400 font-mono">Date: {formatDate(row.date)}</p>
        </div>
      )
    },
    {
      header: "Type",
      accessorKey: "type",
      render: (val) => (
        <Badge variant={val === "Customer Receipt" ? "emerald" : "blue"}>
          {val}
        </Badge>
      )
    },
    {
      header: "Party / Customer / Vendor",
      accessorKey: "partyName",
      render: (val) => <span className="font-medium text-slate-800 dark:text-slate-200">{val}</span>
    },
    {
      header: "Payment Method",
      accessorKey: "paymentMethod",
      render: (val) => <span className="font-mono text-slate-600 dark:text-slate-400">{val}</span>
    },
    {
      header: "Settlement Amount",
      accessorKey: "amount",
      align: "right",
      render: (val, row) => (
        <span
          className={`font-mono font-bold ${
            row.type === "Customer Receipt" ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"
          }`}
        >
          {row.type === "Customer Receipt" ? "+" : "-"}{formatCurrency(val)}
        </span>
      )
    }
  ];

  const expenseColumns = [
    {
      header: "Expense ID",
      accessorKey: "id",
      render: (val, row) => (
        <div>
          <span className="font-bold font-mono text-slate-900 dark:text-white">{val}</span>
          <p className="text-[10px] text-slate-400">Date: {formatDate(row.date)}</p>
        </div>
      )
    },
    {
      header: "Category",
      accessorKey: "category",
      render: (val) => <Badge variant="purple">{val}</Badge>
    },
    {
      header: "Paid To",
      accessorKey: "paidTo",
      render: (val) => <span className="font-medium text-slate-800 dark:text-slate-200">{val}</span>
    },
    {
      header: "Amount (₹)",
      accessorKey: "amount",
      align: "right",
      render: (val) => (
        <span className="font-bold font-mono text-rose-600 dark:text-rose-400">
          -{formatCurrency(val)}
        </span>
      )
    },
    {
      header: "Notes",
      accessorKey: "notes",
      render: (val) => <span className="text-slate-500 text-[11px]">{val}</span>
    }
  ];

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-500" />
            Financial Ledger, P&L Summary & Cash Flow
          </h2>
          <p className="text-xs text-slate-500">
            Real-time income statement, customer receipts, supplier settlements, and categorized operational overheads
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsExpenseOpen(true)}
            className="inline-flex items-center gap-1 px-3.5 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-300 text-xs font-semibold rounded-xl transition"
          >
            <Plus className="w-4 h-4" />
            Log Expense
          </button>
          <button
            onClick={() => setIsPaymentOpen(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl shadow-lg shadow-emerald-600/30 transition"
          >
            <Plus className="w-4 h-4" />
            Record Payment
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex p-1 bg-slate-100 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-xs w-fit">
        <button
          onClick={() => setActiveTab("pl")}
          className={`px-3.5 py-1.5 rounded-lg font-semibold transition ${
            activeTab === "pl" ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs" : "text-slate-500 hover:text-slate-800"
          }`}
        >
          P&L Financial Summary
        </button>
        <button
          onClick={() => setActiveTab("payments")}
          className={`px-3.5 py-1.5 rounded-lg font-semibold transition ${
            activeTab === "payments" ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs" : "text-slate-500 hover:text-slate-800"
          }`}
        >
          Payments & Receipts ({payments.length})
        </button>
        <button
          onClick={() => setActiveTab("expenses")}
          className={`px-3.5 py-1.5 rounded-lg font-semibold transition ${
            activeTab === "expenses" ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs" : "text-slate-500 hover:text-slate-800"
          }`}
        >
          OPEX Expenses ({expenses.length})
        </button>
      </div>

      {/* P&L Statement */}
      {activeTab === "pl" && (
        <div className="rounded-3xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-xl space-y-6">
          <div className="border-b border-slate-100 dark:border-slate-800 pb-4">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white font-display">
              Commercial Profit & Loss (P&L) Statement
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Computed live from sales, procurement & operating expenses</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-2xl bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200/60 dark:border-emerald-900/40 space-y-1">
              <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">Total Sales Revenue:</span>
              <p className="text-2xl font-bold font-mono text-emerald-700 dark:text-emerald-300">
                {formatCurrency(metrics.totalSalesAmount)}
              </p>
              <p className="text-[11px] text-slate-500">Retail walk-in & Wholesale B2B</p>
            </div>

            <div className="p-4 rounded-2xl bg-sky-50/50 dark:bg-sky-950/20 border border-sky-200/60 dark:border-sky-900/40 space-y-1">
              <span className="text-xs font-semibold text-sky-700 dark:text-sky-400">Gross Margin & Profit:</span>
              <p className="text-2xl font-bold font-mono text-sky-700 dark:text-sky-300">
                +{formatCurrency(metrics.totalProfit)}
              </p>
              <p className="text-[11px] text-slate-500">Average margin: {metrics.grossMargin}%</p>
            </div>

            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 space-y-1">
              <span className="text-xs font-semibold text-slate-500">Total OPEX Overheads:</span>
              <p className="text-2xl font-bold font-mono text-rose-600 dark:text-rose-400">
                -{formatCurrency(metrics.totalExpensesAmount)}
              </p>
              <p className="text-[11px] text-slate-500">Rent, freight, utilities & handling</p>
            </div>
          </div>

          {/* Net Profit Big Banner */}
          <div className="p-5 rounded-2xl bg-gradient-to-r from-slate-900 to-emerald-950 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-400 font-mono">
                Estimated Net Business Profit
              </span>
              <h4 className="text-3xl font-extrabold font-display mt-0.5">
                {formatCurrency(metrics.estimatedNetProfit)}
              </h4>
              <p className="text-xs text-slate-300">Gross Profit (₹{metrics.totalProfit.toLocaleString("en-IN")}) minus Overheads (₹{metrics.totalExpensesAmount.toLocaleString("en-IN")})</p>
            </div>

            <div className="text-right">
              <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 px-3 py-1 rounded-full text-xs font-bold font-mono">
                Solvent & Profitable
              </span>
            </div>
          </div>
        </div>
      )}

      {activeTab === "payments" && (
        <DataTable
          columns={paymentColumns}
          data={payments}
          title="Customer Receipts & Supplier Payments Ledger"
          subtitle="Settlement vouchers and bank reconciliation records"
          filename="kirana_financial_payments"
        />
      )}

      {activeTab === "expenses" && (
        <DataTable
          columns={expenseColumns}
          data={expenses}
          title="Categorized Operational Expenses (OPEX)"
          subtitle="All operating overheads, rent, transport, and utilities"
          filename="kirana_expenses_ledger"
        />
      )}

      <PaymentModal
        isOpen={isPaymentOpen}
        onClose={() => setIsPaymentOpen(false)}
      />

      <ExpenseModal
        isOpen={isExpenseOpen}
        onClose={() => setIsExpenseOpen(false)}
      />
    </div>
  );
}
